import React from 'react';
import { X, FileText, AlertCircle, Clock } from 'lucide-react';
import { useModalScrollLock } from '../hooks/useModalScrollLock';

const KpiDetailsModal = ({ isOpen, onClose, title, records, onUpdatePayment, onViewDocument, type }) => {
  useModalScrollLock(isOpen);

  if (!isOpen) return null;

  const formatCurrency = (amount) => {
    return '₹' + Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-4 text-white flex justify-between items-center shrink-0 bg-[#0A5F38]">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg">
              {type === 'overdue' ? <AlertCircle className="h-6 w-6 text-white" /> : <FileText className="h-6 w-6 text-white" />}
            </div>
            <div>
              <h2 className="text-xl font-bold">{title}</h2>
              <p className="text-white/80 text-sm">{records.length} records found</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50">
          {records.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-xs font-bold border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">Document Details</th>
                      <th className="px-6 py-4">Customer</th>
                      <th className="px-6 py-4 text-right">Total Amount</th>
                      <th className="px-6 py-4 text-right">Remaining Balance</th>
                      {type === 'overdue' && (
                        <th className="px-6 py-4 text-center">Overdue Days</th>
                      )}
                      <th className="px-6 py-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.map((record, idx) => (
                      <tr key={record.id || idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <button 
                            onClick={() => onViewDocument && onViewDocument(record)}
                            className="flex flex-col text-left hover:opacity-75 transition-opacity"
                          >
                            <span className="font-bold text-[#0A5F38] hover:underline">
                              {record.documentNumber}
                            </span>
                            <span className="text-xs text-slate-500 capitalize">
                              {record.documentType === 'invoice' ? 'Invoice' : 'Closed Order'}
                            </span>
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-800">{record.customerName || 'Unknown'}</div>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900">
                          {formatCurrency(record.totalAmount)}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-amber-600">
                          {formatCurrency(record.pendingAmount)}
                        </td>
                        {type === 'overdue' && (
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <Clock className="w-3 h-3 mr-1" />
                              {record.overdueDays} days
                            </span>
                          </td>
                        )}
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => onUpdatePayment(record)}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition-colors shadow-sm"
                          >
                            UPDATE PAYMENT
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FileText className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-lg font-medium">No records found for this KPI.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white p-4 border-t border-slate-200 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors"
          >
            CLOSE
          </button>
        </div>

      </div>
    </div>
  );
};

export default KpiDetailsModal;
